import PropTypes from 'prop-types';
import { dateUtils } from './DateUtils';

/**
 * Per-day availability status as returned by the `checkinAvailability` and `checkoutAvailability` APIs
 * (mirrors backend `DateAvailabilityStatus`). Which optional fields are present depends on `reason`.
 * @typedef {object} DateAvailabilityStatus
 * @property {boolean} available
 * @property {string} [reason] - a {@link DATE_UNAVAILABILITY_REASON} value; present when `available` is false
 * @property {number} [minLos] - minimum nights required (`MIN_LOS_NOT_MET`, `NO_VALID_STAY`)
 * @property {boolean} [multiplyMinLos] - the stay must also be a multiple of `minLos`
 * @property {number[]} [allowedCheckinDaysOfWeek] - ISO weekdays 1-7 (`CHECKIN_DAY_NOT_ALLOWED`)
 * @property {number[]} [allowedCheckoutDaysOfWeek] - ISO weekdays 1-7 (`CHECKOUT_DAY_NOT_ALLOWED`, `NO_VALID_STAY`)
 */

/**
 * Map of ISO date string (`yyyy-MM-dd`, see {@link dateToIsoKey}) to availability status.
 * @typedef {Record<string, DateAvailabilityStatus>} AvailabilityByDateMap
 */

/**
 * Mirrors backend `DateUnavailabilityReason`, plus UI-only `MAX_LOS_EXCEEDED`
 * for dates beyond the checkout availability map.
 */
export const DATE_UNAVAILABILITY_REASON = {
  NO_INVENTORY: 'NO_INVENTORY',
  RATE_PLAN_UNAVAILABLE: 'RATE_PLAN_UNAVAILABLE',
  CHECKIN_DAY_NOT_ALLOWED: 'CHECKIN_DAY_NOT_ALLOWED',
  CHECKOUT_DAY_NOT_ALLOWED: 'CHECKOUT_DAY_NOT_ALLOWED',
  MIN_LOS_NOT_MET: 'MIN_LOS_NOT_MET',
  NO_VALID_STAY: 'NO_VALID_STAY',
  MAX_LOS_EXCEEDED: 'MAX_LOS_EXCEEDED',
};

/** PropTypes shape of {@link DateAvailabilityStatus}. */
export const dateAvailabilityStatusPropType = PropTypes.shape({
  available: PropTypes.bool.isRequired,
  reason: PropTypes.string,
  minLos: PropTypes.number,
  multiplyMinLos: PropTypes.bool,
  allowedCheckinDaysOfWeek: PropTypes.arrayOf(PropTypes.number),
  allowedCheckoutDaysOfWeek: PropTypes.arrayOf(PropTypes.number),
});

/**
 * Key of a date in an {@link AvailabilityByDateMap} (`yyyy-MM-dd`).
 * @param {Date} date
 * @returns {string}
 */
export const dateToIsoKey = (date) => dateUtils.getDateString(date);
